/**
 * A held auxiliary mouse button suppresses native pointerdown/up for subsequent
 * left clicks. Some embedded browsers report that button as held indefinitely.
 * Recover only those canvas gestures, retaining the SDK's normal DOM handlers.
 */
export function installMouseButtonRecovery(container: HTMLElement) {
  const doc = container.ownerDocument;
  const view = doc.defaultView!;
  let pointerId = 1;
  let primaryDown = false;
  let forwarding = false;
  let gesture: { target: Element; last: MouseEvent } | null = null;

  const forward = (type: string, event: MouseEvent) => {
    if (!gesture) return;
    gesture.last = event;
    forwarding = true;
    try {
      gesture.target.dispatchEvent(
        new view.PointerEvent(type, {
          bubbles: true,
          cancelable: true,
          composed: true,
          pointerId,
          pointerType: "mouse",
          isPrimary: true,
          clientX: event.clientX,
          clientY: event.clientY,
          screenX: event.screenX,
          screenY: event.screenY,
          button: type === "pointermove" ? -1 : 0,
          buttons: type === "pointerup" ? 0 : 1,
          pressure: type === "pointerup" ? 0 : 0.5,
          ctrlKey: event.ctrlKey,
          metaKey: event.metaKey,
          shiftKey: event.shiftKey,
          altKey: event.altKey,
        }),
      );
    } finally {
      forwarding = false;
    }
  };
  const finish = () => {
    if (gesture) forward("pointerup", gesture.last);
    gesture = null;
    primaryDown = false;
  };
  const pointer = (event: PointerEvent) => {
    if (forwarding || event.pointerType !== "mouse") return;
    pointerId = event.pointerId;
    if (gesture) {
      // mousemove follows pointermove: forward only one movement stream.
      event.stopImmediatePropagation();
      if (event.type === "pointercancel") finish();
      return;
    }
    if (event.type === "pointerdown" && event.button === 0) primaryDown = true;
    if (event.type === "pointerup" || event.type === "pointercancel")
      primaryDown = false;
  };
  const down = (event: MouseEvent) => {
    if (primaryDown || gesture || event.button !== 0 || event.defaultPrevented)
      return;
    // Left + Back/Forward only; never take over middle/right-button navigation.
    if (!(event.buttons & 1) || !(event.buttons & 24) || event.buttons & 6)
      return;
    const target = event.target;
    if (
      !(target instanceof view.Element) ||
      !container.contains(target) ||
      !target.closest(".canvas-view") ||
      target.closest('button,a,input,textarea,select,[contenteditable="true"]')
    )
      return;
    gesture = { target, last: event };
    forward("pointerdown", event);
  };
  const move = (event: MouseEvent) => {
    if (!gesture) return;
    if (!(event.buttons & 1)) {
      finish();
      return;
    }
    forward("pointermove", event);
  };
  const up = (event: MouseEvent) => {
    if (!gesture || event.button !== 0) return;
    forward("pointerup", event);
    gesture = null;
  };
  const pointerEvents = [
    "pointerdown",
    "pointermove",
    "pointerup",
    "pointercancel",
  ] as const;
  const capture = { capture: true };
  pointerEvents.forEach((name) => doc.addEventListener(name, pointer, capture));
  doc.addEventListener("mousedown", down, capture);
  doc.addEventListener("mousemove", move, capture);
  doc.addEventListener("mouseup", up, capture);
  view.addEventListener("blur", finish);
  return () => {
    finish();
    pointerEvents.forEach((name) =>
      doc.removeEventListener(name, pointer, capture),
    );
    doc.removeEventListener("mousedown", down, capture);
    doc.removeEventListener("mousemove", move, capture);
    doc.removeEventListener("mouseup", up, capture);
    view.removeEventListener("blur", finish);
  };
}
